/** Machine actions are available only for Nebius inventory. */

import type { Machine } from "@/lib/types";

export function isManagedMachine(machine: Pick<Machine, "provider">): boolean {
  return machine.provider === "nebius";
}

/**
 * Whether `currentUserId` may stop `machine` — the single source of truth for
 * both the per-row ⋯ Stop and the toolbar's bulk Stop. Mirrors the backend
 * `stop_machine` rule (`api/machines.py`): a running, stoppable, unlocked
 * machine can be stopped by anyone while it's free, or only by its owner / an
 * admin once it's allocated.
 */
export function canStopMachine(machine: Machine, currentUserId: number, isAdmin: boolean): boolean {
  return (
    machine.cloud_status === "running" &&
    isManagedMachine(machine) &&
    !machine.is_locked &&
    (machine.allocated_to == null || machine.allocated_to === currentUserId || isAdmin)
  );
}

/**
 * Mirrors `_set_disposable_in_session` (`api/machines.py`): holder-only — an
 * admin on someone else's lease gets nothing — on a running, unlocked machine.
 */
export function canMarkDisposable(machine: Machine, currentUserId: number): boolean {
  return (
    machine.allocated_to === currentUserId &&
    machine.cloud_status === "running" &&
    !machine.is_locked &&
    !machine.disposable
  );
}

/** Clearing has no running/lock gate — only the holder check. */
export function canClearDisposable(machine: Machine, currentUserId: number): boolean {
  return machine.allocated_to === currentUserId && Boolean(machine.disposable);
}

export function canToggleDisposable(machine: Machine, currentUserId: number): boolean {
  return canMarkDisposable(machine, currentUserId) || canClearDisposable(machine, currentUserId);
}

/**
 * Mirrors the body-carrying `POST /{id}/disposable` rule: holder-only, running,
 * unlocked. No already-disposable guard — the options are re-postable.
 */
export function canEditDisposableOptions(machine: Machine, currentUserId: number): boolean {
  return (
    machine.allocated_to === currentUserId &&
    machine.cloud_status === "running" &&
    !machine.is_locked
  );
}

/** True when the holder pinned the disposable lease to a specific set of users. */
export function isDisposableRestricted(machine: Machine): boolean {
  return Boolean(machine.disposable) && (machine.disposable_allowed_user_ids?.length ?? 0) > 0;
}

function disposableFromMs(machine: Machine): number | null {
  if (!machine.disposable_from) return null;
  const parsed = Date.parse(machine.disposable_from);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Mirrors `is_disposable_now` (`alloc.py`): marked disposable, and the deferred
 * window (if any) has opened. The allowlist is a separate check.
 */
export function isDisposableNow(machine: Machine, now: number = Date.now()): boolean {
  if (!machine.disposable) return false;
  const opensAt = disposableFromMs(machine);
  return opensAt == null || opensAt <= now;
}

export function isDisposablePending(machine: Machine, now: number = Date.now()): boolean {
  return Boolean(machine.disposable) && !isDisposableNow(machine, now);
}

export function pendingDisposableFrom(machine: Machine, now: number = Date.now()): string | null {
  return isDisposablePending(machine, now) ? (machine.disposable_from ?? null) : null;
}

/**
 * Mirrors `loose_acquire_machine` (`api/machines.py`): someone else's disposable
 * lease whose window is open, gated by the holder's allowlist when they set one
 * (empty = anyone).
 */
export function canLooseAcquire(
  machine: Machine,
  currentUserId: number,
  now: number = Date.now(),
): boolean {
  if (machine.allocated_to == null || machine.allocated_to === currentUserId) return false;
  if (!isDisposableNow(machine, now)) return false;
  const allowedUserIds = machine.disposable_allowed_user_ids ?? [];
  return allowedUserIds.length === 0 || allowedUserIds.includes(currentUserId);
}

/**
 * Mirrors `_ensure_can_toggle_eod_skip` (`api/machines.py`): allowed on an
 * allocated machine when the current user is the owner or an admin. Unlike
 * disposable there is no running-state gate.
 */
export function canToggleEodSkip(
  machine: Machine,
  currentUserId: number,
  isAdmin: boolean,
): boolean {
  return machine.allocated_to != null && (machine.allocated_to === currentUserId || isAdmin);
}
